import type { TileConfig } from '@/components/strategy/MapTileLayer'
import type { LumaStats } from '@/lib/mapView'
import type { GameCalibration } from '@/lib/gameMap'

/** /api/maps/list kaydı — kaynağı backend/strategies/maps.py */
export interface MapInfo {
  name: string
  label: string
  width: number
  height: number
  /** Tile'lar yüklenene kadar altta duran tek parça görüntü. */
  base: string
  thumb: string
  /**
   * Haritanın oyundan gelen 1920×1080 tanıtım görseli — `base`/`thumb`'ın
   * aksine tepeden harita değil, haritanın manzarası.
   *
   * **Harita adı görselin sol altına gömülü**, yani üstüne ikinci bir ad
   * basılmaz ve görsel adın durduğu yerden kırpılmaz. Görseli olmayan harita
   * için alan gelmez; kart `thumb`'a düşer.
   */
  hero?: string
  tiles: TileConfig
  /** Gri tonlama parlaklık istatistiği — taktiksel görünümün filtresini çözer. */
  luma?: LumaStats
  /**
   * Oyun içi koordinat kalibrasyonu — harita pikselini oyuncunun oyunda okuduğu
   * koordinata ve metreye bağlar. Mortar hesap makinesi bunu ister; strateji
   * editörü kendi 0..1080 dünyasında çalıştığı için bakmaz.
   */
  game?: GameCalibration
}

/**
 * Kataloğun iki kapısı var, ikisi de aynı kaynağı (backend/strategies/maps.py)
 * döndürür; ayrım yalnızca kimin sorabileceğinde:
 *
 * - `/api/maps/list` — dashboard oturumu ister (strateji editörü, SP editörü,
 *   maç formu).
 * - `/api/home/maps` — halka açık; turksletloose.com'daki mortar hesap
 *   makinesi oturumsuz ziyaretçiye de açık, o yüzden ayrı bir uca bakar.
 *
 * Her kapı kendi önbelleğini tutar: sayfa ikisini birden kullanmaz.
 */
const URLS = {
  dashboard: '/api/maps/list',
  public: '/api/home/maps',
} as const

export type MapScope = keyof typeof URLS

const caches: Partial<Record<MapScope, Promise<MapInfo[]>>> = {}

/**
 * Harita kataloğunu getirir. Katalog süreç boyunca sabit olduğu için sonuç
 * önbelleğe alınır — birden çok bileşen aynı anda çağırsa bile tek istek gider.
 */
export function fetchMaps(scope: MapScope = 'dashboard'): Promise<MapInfo[]> {
  const url = URLS[scope]
  let cache = caches[scope]
  if (!cache) {
    cache = fetch(url, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`${url} ${r.status}`)
        return r.json()
      })
      .then((rows: MapInfo[]) => (Array.isArray(rows) ? rows : []))
      .catch(err => {
        delete caches[scope]   // kalıcı boş katalogda takılı kalmayalım
        throw err
      })
    caches[scope] = cache
  }
  return cache
}

/** Harita adını kullanıcıya gösterilecek etikete çevirir. */
export function mapLabel(maps: MapInfo[], name: string): string {
  return maps.find(m => m.name === name)?.label ?? name
}
